import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DIET_ORDER_STATUS_LABELS } from '@wodi/domain';
import type { DietOrderView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';
import { useState } from 'react';

import { dietOrderEnteredInError, kitchenKeys, stayDietQuery } from '../api/kitchen.ts';
import { useOnline } from '../hooks/use-online.ts';
import { dateTimeText, errorText, LoadFailed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { TextDialog } from '../operations/text-dialog.tsx';
import { toasts } from '../toasts.ts';
import { DietDialog } from './diet-dialog.tsx';
import { nbmUntilWords } from './words.tsx';

// The stay's diet (docs/plans/phase3-kitchen-and-housekeeping.md §1.4): the current order in
// words, or "No diet ordered", with every earlier version. Diet orders are never edited or
// deleted: they are changed (a new version) or entered in error with a reason.

export function nbmWords(order: DietOrderView): string | null {
  if (!order.nilByMouth || order.nbmFrom === null) return null;
  return `Nil by mouth from ${dateTimeText(order.nbmFrom)}. ${nbmUntilWords(order.nbmUntil)}.`;
}

function CurrentDiet({ order }: { order: DietOrderView | null }) {
  if (order === null)
    return <p className={styles.meta}>No diet ordered. The kitchen asks the ward.</p>;
  const nbm = nbmWords(order);
  return (
    <dl className={styles.facts}>
      <dt>Diet</dt>
      <dd>{order.words}</dd>
      {nbm === null ? null : (
        <>
          <dt>Nil by mouth</dt>
          <dd>{nbm}</dd>
        </>
      )}
      <dt>Note for the kitchen</dt>
      <dd className={styles.prose}>{order.kitchenNote ?? 'None'}</dd>
      <dt>Ordered</dt>
      <dd>{`${dateTimeText(order.orderedAt)} by ${order.orderedBy.displayName}`}</dd>
    </dl>
  );
}

export function DietSection({
  admissionId,
  mayOrder,
}: {
  admissionId: string;
  /** Holds diet.order. */
  mayOrder: boolean;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const diet = useQuery(stayDietQuery(admissionId));
  const [ordering, setOrdering] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const wrong = useMutation({
    mutationFn: (reason: string) => dietOrderEnteredInError(diet.data?.current?.id ?? '', reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: kitchenKeys.all });
      toasts.show({ title: 'Diet entered in error' });
      setCorrecting(false);
    },
  });
  const current = diet.data?.current ?? null;
  const canChange = mayOrder && diet.data?.admitted === true;
  return (
    <Section
      title="Diet"
      description="What the kitchen serves this patient. Changing it keeps the earlier order."
      {...(canChange
        ? {
            actions: (
              <div className={styles.actions}>
                {current === null ? null : (
                  <Button
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      setCorrecting(true);
                    }}
                  >
                    Entered in error
                  </Button>
                )}
                <Button
                  variant="primary"
                  isDisabled={!online}
                  onPress={() => {
                    setOrdering(true);
                  }}
                >
                  {current === null ? 'Order diet' : 'Change diet'}
                </Button>
              </div>
            ),
          }
        : {})}
    >
      {diet.isPending ? <LoadingBlock label="Loading the diet" rows={2} /> : null}
      {diet.isError ? (
        <LoadFailed
          error={diet.error}
          fallback="The diet couldn't be loaded. Try again."
          onRetry={() => void diet.refetch()}
        />
      ) : null}
      {diet.isSuccess ? (
        <div className={styles.form}>
          <CurrentDiet order={current} />
          {diet.data.history.length > (current === null ? 0 : 1) ? (
            <Table flat dense aria-label="Diet history">
              <TableHeader>
                <Column isRowHeader>Ordered</Column>
                <Column>Diet</Column>
                <Column>Status</Column>
                <Column>Changed</Column>
              </TableHeader>
              <TableBody items={diet.data.history.filter((h) => h.status !== 'active')}>
                {(h) => (
                  <Row id={h.id}>
                    <Cell>{`${dateTimeText(h.orderedAt)}, ${h.orderedBy.displayName}`}</Cell>
                    <Cell>{nbmWords(h) === null ? h.words : `${h.words}. Nil by mouth.`}</Cell>
                    <Cell>
                      <Tag state="neutral">{DIET_ORDER_STATUS_LABELS[h.status]}</Tag>
                    </Cell>
                    <Cell>
                      {h.statusChangedAt === null
                        ? ''
                        : `${dateTimeText(h.statusChangedAt)}${h.statusReason === null ? '' : `: ${h.statusReason}`}`}
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          ) : null}
        </div>
      ) : null}
      {ordering ? (
        <DietDialog
          admissionId={admissionId}
          current={current}
          onClose={() => {
            setOrdering(false);
          }}
        />
      ) : null}
      <TextDialog
        title="Diet entered in error"
        label="Reason"
        description="At least 5 characters. The order stays in the history, marked entered in error."
        minLength={5}
        maxLength={500}
        isOpen={correcting}
        onOpenChange={setCorrecting}
        confirmLabel="Enter in error"
        pendingLabel="Saving…"
        isPending={wrong.isPending}
        error={wrong.error === null ? null : errorText(wrong.error, "It wasn't saved. Try again.")}
        onConfirm={(reason) => {
          wrong.mutate(reason);
        }}
      />
    </Section>
  );
}
